import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { flushSync } from "react-dom";

/*
 * Closes a dialog whenever the user navigates elsewhere. Plain in-app
 * navigation is caught by `location.key`, which changes on every history
 * entry. A cross-document back/forward (e.g. after the browser loaded a
 * typed URL) instead restores a frozen bfcache snapshot verbatim and never
 * fires `popstate`, so `location.key` never changes for it — `pagehide`
 * (just before the freeze, so the snapshot itself has nothing open) and
 * `pageshow`/`persisted` (a fallback, after a restore) catch that case.
 * `flushSync` forces the pagehide close to commit before the freeze, since a
 * plain setState there could otherwise still be batched past that point.
 *
 * `close` is read via a ref so the window listeners are attached once, not
 * re-attached on every render of the caller.
 */
export function useCloseOnNavigate(close) {
  const closeRef = useRef(close);
  closeRef.current = close;

  const location = useLocation();

  useEffect(() => {
    closeRef.current();
  }, [location.key]);

  useEffect(() => {
    const handlePageHide = () => flushSync(closeRef.current);
    const handlePageShow = (event) => {
      if (event.persisted) flushSync(closeRef.current);
    };
    window.addEventListener("pagehide", handlePageHide);
    window.addEventListener("pageshow", handlePageShow);
    return () => {
      window.removeEventListener("pagehide", handlePageHide);
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, []);
}
